<template>
    <div class="user-action-form">
        <b-card>
            <h2>{{$t('action.edit_action')}}</h2>
            <b-form @submit.prevent="handleSubmit" :validated="validated" novalidate>
                <b-form-group :label="$t('action.name')" label-for="action-name">
                    <b-form-input
                        id="action-name"
                        v-model="formFields.name"
                        required
                        @input="removeError('name')">
                    </b-form-input>
                    <b-form-invalid-feedback :state="!formErrors.has('name')">
                        {{ formErrors.first('name') }}
                    </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group :label="$t('action.action_type')" label-for="action-type">
                    <b-form-radio-group
                        class="form-input d-flex"
                        id="action-type"
                        v-model="formFields.action_type"
                        :options="actionTypeOptions"
                        name="action_type"
                        @input="removeError('action_type')"
                        stacked
                    ></b-form-radio-group>
                    <b-form-invalid-feedback :state="!formErrors.has('action_type')">
                        {{ formErrors.first('action_type') }}
                    </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group
                    id="notification_type"
                    :label="$t('action.notification_type')"
                    label-for="notification_type"
                >
                    <treeselect
                        multiple
                        placeholder="Select a action type"
                        v-model="formFields.notification_type"
                        class="form-input"
                        :class="formErrors.has('notification_type') ? 'error-input' : ''"
                        :options="notificationTypes"
                        :normalizer="normalizer"
                        required
                        @input="removeError('notification_type')"
                    ></treeselect>
                    <b-form-invalid-feedback :state="!formErrors.has('notification_type')">
                        {{ formErrors.first('notification_type') }}
                    </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group
                    v-if="formFields.action_type !== 'manual'"
                    :label="$t('action.select_triggers')"
                    label-for="trigger_ids"
                    required
                >
                    <treeselect
                        multiple
                        placeholder="Select a trigger"
                        v-model="formFields.trigger_ids"
                        class="form-input"
                        :class="formErrors.has('trigger_ids') ? 'error-input' : ''"
                        :options="triggerOptions"
                        :normalizer="normalizer"
                        required
                        @input="removeError('trigger_ids')"
                    ></treeselect>
                    <b-form-invalid-feedback :state="!formErrors.has('trigger_ids')">
                        {{ formErrors.first('trigger_ids') }}
                    </b-form-invalid-feedback>

                </b-form-group>
                <b-form-group
                    :label="$t('action.select_action')"
                    label-for="action_slug"
                    required
                >
                    <treeselect
                        placeholder="Select an Action"
                        v-model="formFields.action_slug"
                        class="form-input"
                        :class="formErrors.has('action_slug') ? 'error-input' : ''"
                        :options="actionOptions"
                        :normalizer="normalizer"
                        required
                        @input="removeError('action_slug')"
                    ></treeselect>
                    <b-form-invalid-feedback :state="!formErrors.has('action_slug')">
                        {{ formErrors.first('action_slug') }}
                    </b-form-invalid-feedback>

                </b-form-group>

                <!-- Manual Time Selection Section -->
                <div v-if="formFields.action_type === 'manual'" class="manual-selection-section">
                    <h4 class="mb-3">{{ $t('action.manual_time_selection') }}</h4>

                    <!-- Hotel Group Selection -->
                    <b-form-group
                        :label="$t('action.select_hotel_group')"
                        label-for="hotel_group"
                        class="mb-3"
                    >
                        <treeselect
                            placeholder="Select Hotel Group"
                            v-model="formFields.hotel_group_id"
                            class="form-input"
                            :class="formErrors.has('hotel_group_id') ? 'error-input' : ''"
                            :options="hotelGroupOptions"
                            :normalizer="normalizer"
                            @input="onHotelGroupChange"
                        ></treeselect>
                        <b-form-invalid-feedback :state="!formErrors.has('hotel_group_id')">
                            {{ formErrors.first('hotel_group_id') }}
                        </b-form-invalid-feedback>
                    </b-form-group>

                    <!-- Hotels Display -->
                    <div v-if="selectedHotels.length > 0" class="hotels-section mb-4">
                        <h5 class="mb-3">{{ $t('action.available_hotels') }}</h5>
                        <div class="row">
                            <div v-for="hotel in selectedHotels" :key="hotel.id" class="col-md-6 col-lg-4 mb-3">
                                <b-card class="hotel-card h-100">
                                    <div class="hotel-image mb-2" v-if="hotel.galleries && hotel.galleries.length > 0">
                                        <img :src="hotel.galleries[0].path" :alt="hotel.name" class="img-fluid rounded" style="height: 150px; width: 100%; object-fit: cover;">
                                    </div>
                                    <h6 class="hotel-name">{{ hotel.name }}</h6>
                                    <p class="hotel-location text-muted small">
                                        <i class="fas fa-map-marker-alt"></i>
                                        {{ hotel.city?.name }}, {{ hotel.state?.name }}
                                    </p>

                                    <!-- Facilities -->
                                    <div v-if="hotel.amenities && hotel.amenities.length > 0" class="facilities-section mb-2">
                                        <small class="text-muted d-block mb-1">{{ $t('action.facilities') }}:</small>
                                        <div class="facilities-list">
                                            <b-form-checkbox
                                                v-for="amenity in hotel.amenities"
                                                :key="amenity.facility?.id"
                                                :value="amenity.facility?.id"
                                                v-model="selectedFacilityIds"
                                                class="facility-checkbox"
                                                :id="`facility-${amenity.facility?.id}`"
                                            >
                                                 <span class="badge bg-info mr-1">
                                                    {{ amenity.facility?.name }}
                                                </span>
                                            </b-form-checkbox>
                                        </div>
                                    </div>

                                    <!-- Offers -->
                                    <div v-if="hotel.offers && hotel.offers.length > 0" class="offers-section">
                                        <small class="text-muted d-block mb-1">{{ $t('action.offers') }}:</small>
                                        <div class="offers-list">
                                            <b-form-checkbox
                                                v-for="offer in hotel.offers"
                                                :key="offer.id"
                                                :value="offer.id"
                                                v-model="selectedOfferIds"
                                                class="offer-checkbox"
                                                :id="`offer-${offer.id}`"
                                            >
                                                <span class="badge bg-success mr-1">
                                                    {{ offer.name }}
                                                </span>
                                            </b-form-checkbox>
                                        </div>
                                    </div>

                                    <!-- Debug info -->
                                    <div v-if="hotel.offers" class="debug-info mt-2">
                                        <small class="text-muted">Debug: {{ hotel.offers.length }} offers found</small>
                                    </div>
                                </b-card>
                            </div>
                        </div>
                    </div>

                    <!-- Loading State -->
                    <div v-if="loadingHotels" class="text-center py-4">
                        <b-spinner class="mr-2"></b-spinner>
                        {{ $t('action.loading_hotels') }}
                    </div>

                    <!-- No Hotels Message -->
                    <div v-if="!loadingHotels && selectedHotels.length === 0 && formFields.hotel_group_id" class="alert alert-info">
                        <i class="fas fa-info-circle"></i>
                        {{ $t('action.no_hotels_found') }}
                    </div>
                </div>

                <b-form-group
                    v-if="formFields.action_type === 'automatic'"
                    :label="$t('action.start_date')"
                    label-for="start-date"
                >
                    <b-form-datepicker
                        id="start_date_picker"
                        v-model="formFields.start_date"
                        reset-button
                        today-button
                        locale="en"
                        class="w-100"
                        :min="today"
                    >
                    </b-form-datepicker>
                    <b-form-invalid-feedback :state="!formErrors.has('start_date')">
                        {{ formErrors.first('start_date') }}
                    </b-form-invalid-feedback>
                </b-form-group>
                <b-form-group
                    v-if="formFields.action_type === 'automatic'"
                    :label="$t('action.end_date')"
                    label-for="end_date_picker"
                >
                    <b-form-datepicker
                        id="end_date_picker"
                        v-model="formFields.end_date"
                        reset-button
                        today-button
                        locale="en"
                        :min="minEndDate"
                        class="w-100"
                    >
                    </b-form-datepicker>
                    <b-form-invalid-feedback :state="!formErrors.has('end_date')">
                        {{ formErrors.first('end_date') }}
                    </b-form-invalid-feedback>
                </b-form-group>
                <b-tabs v-model="activeTab" class="mt-3">
                    <b-tab :title="$t('action.email_content')" active>
                        <b-form-group label-for="email-content">
                            <ckeditor
                                id="email-content"
                                :editor="editor"
                                v-model="formFields.email_content"
                                :config="editorConfig"
                                @input="removeError('sms_content')"
                            ></ckeditor>
                            <b-form-invalid-feedback :state="!formErrors.has('email_content')">
                                {{ formErrors.first('email_content') }}
                            </b-form-invalid-feedback>
                        </b-form-group>
                    </b-tab>

                    <b-tab :title="$t('action.sms_content')">
                        <b-form-group label-for="sms-content">
                            <ckeditor id="sms-content"
                                      :editor="editor"
                                      v-model="formFields.sms_content"
                                      :config="editorConfig"
                                      @input="removeError('sms_content')"
                            >
                            </ckeditor>
                            <b-form-invalid-feedback :state="!formErrors.has('sms_content')">
                                {{ formErrors.first('sms_content') }}
                            </b-form-invalid-feedback>
                        </b-form-group>
                    </b-tab>
                </b-tabs>

                <div class="d-flex gap-2 mt-4">
                    <b-button
                        type="submit"
                        variant="primary"
                        :disabled="loader"
                    >
                        <b-spinner small v-if="loader" class="mr-1"></b-spinner>
                        {{$t('action.save')}}
                    </b-button>
                    <b-button variant="secondary" @click="cancel">{{$t('action.cancel')}}</b-button>
                </div>
            </b-form>
        </b-card>
    </div>
</template>

<script>
import { request } from "@/Util/Request";
import Error from "@/Util/Error";
import "vue-tel-input/dist/vue-tel-input.css";
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import 'vue2-timepicker/dist/VueTimepicker.css';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { mapGetters } from "vuex";

const FORM_STATE = {
    name: null,
    trigger_ids: [],
    notification_type: [],
    email_content:null,
    sms_content:null,
    start_date: null,
    end_date: null,
    action_type: null,
    action_slug: null,
    hotel_group_id: null,
    is_manual: false
};

export default {
    name: 'EditAction',
    components: {
        Treeselect,
    },
    data() {
        return {
            formFields: { ...FORM_STATE },
            formErrors: new Error({}),
            validated: false,
            actionTypeOptions: [],
            actionOptions: [],
            notificationTypes: [],
            triggerOptions:[],
            hotelGroupOptions: [],
            selectedHotels: [],
            loadingHotels: false,
            selectedOfferIds: [],
            selectedFacilityIds: [],
            activeTab: 0,
            editor: ClassicEditor,
            editorConfig: {
                toolbar: [
                    'bold',
                    'italic',
                    'underline',
                    'bulletedList',
                    'numberedList',
                    '|',
                    'undo',
                    'redo'
                ],
                placeholder: 'Enter content here...',
                height: '200px',
                enterMode: 'ENTER_BR',
                shiftEnterMode: 'ENTER_P',
                autoParagraph: false,
                fillEmptyBlocks: false,
            },
            today: new Date().toISOString().split("T")[0],
            actionId: null
        }
    },
    mounted() {
        const id = this.$route.params.id;
        this.actionId = id;
        this.fetchAction(id);
        this.fetchActionDropdowns();
        this.fetchHotelGroups();
    },
    computed: {
        minEndDate() {
            return this.formFields.start_date || this.today;
        },
        ...mapGetters(['loader'])
    },
    methods: {
        async fetchAction(id) {
            try {
                const response = await request({ method: 'get', url: `/actions/${id}` });
                const data = response.data;

                // Set form fields
                this.formFields = { ...data };
                this.formFields.trigger_ids = data.triggers.map(trigger => trigger.id);

                // Set selected offers and facilities
                this.selectedOfferIds = data.selected_offer_ids || [];
                this.selectedFacilityIds = data.selected_facility_ids || [];

                // For manual actions with hotel group, load the hotels
                if (data.action_type === 'manual' && data.hotel_group_id) {
                    await this.onHotelGroupChange(data.hotel_group_id);

                    // Ensure selections are preserved after loading hotels
                    this.$nextTick(() => {
                        this.selectedOfferIds = [...(data.selected_offer_ids || [])];
                        this.selectedFacilityIds = [...(data.selected_facility_ids || [])];
                        console.log('Preserved selections after hotel load');
                    });
                }
            } catch (error) {
                console.error('Error fetching Action data:', error);
            }
        },

        normalizer(node) {
            return {
                id: node.id,
                label: node.label
            };
        },
        resetError() {
            this.formErrors = new Error({});
        },
        async removeError(key) {
            if (typeof key === 'object') {
                for (let i = 0; i < key.length; i++) {
                    this.formErrors.remove(key[i]);
                }
            } else {
                this.formErrors.remove(key);
            }
        },
        async fetchActionDropdowns() {
            try {
                const response = await request({
                    method: 'get',
                    url: `/fetch-action-dropdowns`
                });

                if (response) {
                    this.notificationTypes = response.data.notification_types.map(notificationType => ({
                        id: notificationType,
                        label: notificationType
                    }));
                    this.actionTypeOptions = response.data.action_types.map(actionType => ({
                        text: this._.upperFirst(actionType),
                        value: actionType
                    }));
                    this.triggerOptions = response.data.triggers.map(trigger => ({
                        id: trigger.id,
                        label: trigger.name
                    }));
                    this.actionOptions = response.data.actions.map(actionType => ({
                        id: actionType,
                        label: this._.upperFirst(this._.replace(actionType,/_/g, " "))
                    }));
                }
            } catch (error) {
                console.error('Error fetching product services:', error);
            }
        },
        async fetchHotelGroups() {
            try {
                const response = await request({
                    method: 'get',
                    url: '/hotel-groups'
                });

                if (response && response.data) {
                    this.hotelGroupOptions = response.data.map(group => ({
                        id: group.id,
                        label: group.name
                    }));
                }
            } catch (error) {
                console.error('Error fetching hotel groups:', error);
                this.hotelGroupOptions = [];
            }
        },
        async onHotelGroupChange(hotelGroupId) {

            console.log(hotelGroupId);
            if (!hotelGroupId) {
                this.selectedHotels = [];
                return;
            }

            this.loadingHotels = true;
            this.selectedHotels = [];

            try {
                // Fetch only hotels that have manual facilities or offers
                const response = await request({
                    method: 'get',
                    url: `/public/hotel-groups/${hotelGroupId}/hotels-with-manual-items`
                });

                console.log(response);
                if (response && response.data && response.hotels_with_manual_items) {
                    // The API now returns hotels with manual facilities and offers already included
                    this.selectedHotels = response.hotels_with_manual_items;
                    console.log(this.selectedHotels);
                }
            } catch (error) {
                console.error('Error fetching hotels for group:', error);
                this.selectedHotels = [];
            } finally {
                this.loadingHotels = false;
            }
        },


        cancel() {
            this.$router.back();
        },
        async handleSubmit() {
            try {
                const payload = {
                    ...this.formFields,
                    is_manual: this.formFields.action_type === 'manual',
                    hotel_group_id: this.hotel_group_id,
                    selected_offer_ids: this.selectedOfferIds,
                    selected_facility_ids: this.selectedFacilityIds
                }

                const response = await request({
                    method: 'put',
                    url: `/actions/${this.actionId}`,
                    data: payload
                });

                if (response) {
                    this.formFields = { ...FORM_STATE };
                    this.$router.push(`/admin/actions`).then(() => {
                        this.$nextTick(() => {
                            this.$bvToast.toast('Action Updated Successfully!', {
                                title: 'Success',
                                variant: 'success',
                                solid: true,
                            });
                        });
                    });
                }
            } catch (error) {
                if (error.request && error.request.status && error.request.status === 422) {
                    this.formErrors = new Error(JSON.parse(error.request.responseText).errors);
                    return false;
                } else {
                    this.notifyError();
                }
            }
        }
    }
}
</script>

<style scoped>
.user-action-form {
    padding: 20px;
}

.form-group {
    margin-bottom: 20px;
}

h2 {
    margin-bottom: 1.5rem;
    color: #2c3e50;
    font-size: 2rem;
}

.error-input {
    border-color: #dc3545 !important;
}

.manual-selection-section {
    background-color: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    border: 1px solid #e9ecef;
    margin-top: 20px;
}

.hotel-card {
    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
    border: 1px solid #e9ecef;
}

.hotel-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.hotel-name {
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 8px;
}

.hotel-location {
    font-size: 0.875rem;
    margin-bottom: 12px;
}

.facilities-section, .offers-section {
    margin-bottom: 8px;
}

.facilities-list, .offers-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.hotels-section h5 {
    color: #495057;
    font-weight: 600;
    border-bottom: 2px solid #007bff;
    padding-bottom: 8px;
}

.alert {
    border-radius: 6px;
}

.alert i {
    margin-right: 8px;
}

.facility-checkbox, .offer-checkbox {
    margin-bottom: 8px;
    display: block;
}

.facility-checkbox .form-check-input, .offer-checkbox .form-check-input {
    margin-right: 8px;
}
</style>

